import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/* Porcentaje de descuento de un beneficio.

   Fuente preferida: el campo estructurado `discountPercentage`. Hoy está vacío
   en todos los beneficios de producción, así que en la práctica se cae al
   título ("10% Adicional sobre los descuentos ya existentes").

   Devuelve null cuando el beneficio NO es un porcentaje (meses sin intereses,
   2x1, regalo) o cuando el título es AMBIGUO. La ambigüedad importa: un título
   como "50% DE DESCUENTO + 10 ADICIONAL" mezcla el descuento propio de la
   tienda con el beneficio de VONDI, y quedarse con el primer número haría que
   la calculadora del vendedor prometa 5 veces el ahorro real. Ante la duda se
   prefiere no dar número: la tarjeta del cliente cae al título literal y la
   calculadora omite la tienda. */
export function benefitDiscountPct(benefit) {
  const structured = Number(benefit?.discountPercentage);
  if (structured > 0) return structured;

  // Un beneficio marcado como financiamiento/regalo/otro no es un porcentaje
  // aunque su texto traiga números ("3 o 6 meses sin intereses").
  const perkType = benefit?.perkType;
  if (perkType && perkType !== 'percentage') return null;

  const title = String(benefit?.title || '');
  const candidatos = new Set();

  // "15%", "20 %"
  for (const [, n] of title.matchAll(/(\d{1,3})\s*%/g)) candidatos.add(Number(n));
  // "10 ADICIONAL" (porcentaje escrito sin el símbolo)
  for (const [, n] of title.matchAll(/\b(\d{1,2})\s+adicional/gi)) candidatos.add(Number(n));

  if (candidatos.size !== 1) return null;

  const pct = [...candidatos][0];
  return pct > 0 && pct <= 100 ? pct : null;
}

/* Ajusta el brillo de un color hex (amt: -1 a 1). */
export function adjustColor(hex, amt) {
  if (typeof hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(hex)) return hex;
  const h = hex.replace('#', '');
  const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
  const toHex = (n) => clamp(n).toString(16).padStart(2, '0');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return '#' + toHex(r + amt * 255) + toHex(g + amt * 255) + toHex(b + amt * 255);
}

/* Devuelve el ARRAY de colores de un valor:
   - "c1,c2" o "c1,c2,c3" -> [c1,c2(,c3)]
   - "#c" (sólido)        -> [claro, oscuro] derivados (degradado marcado)
   Soporta 2 o 3 colores. Útil para el editor y el render. */
export function gradientColors(color, fallback = '#0A0A0A') {
  const c = (color || fallback || '#0A0A0A').toString().trim();
  if (c.includes(',')) {
    return c.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 3);
  }
  if (/^#[0-9a-fA-F]{6}$/.test(c)) {
    return [adjustColor(c, 0.30), adjustColor(c, -0.45)];
  }
  return [c];
}

/* Compat: 2 tonos (algunos lugares aún lo usan) */
export function gradientStops(color, fallback = '#0A0A0A') {
  const cols = gradientColors(color, fallback);
  return cols.length >= 2 ? [cols[0], cols[cols.length - 1]] : [cols[0], cols[0]];
}

/* ─── Fondo de tarjeta: SIEMPRE degradado ─────────────────────
   - "c1,c2" / "c1,c2,c3" -> degradado de 2 o 3 paradas.
   - "#c" (sólido)        -> degradado derivado (claro->oscuro).
   - "linear-gradient()"  -> se usa tal cual.
   Se guarda como "#c1,#c2" o "#c1,#c2,#c3" en el mismo campo de color. */
export function cardBackground(color, fallback = '#0A0A0A') {
  const c = (color || fallback);
  if (typeof c !== 'string') return c;
  const v = c.trim();
  if (v.includes('gradient(')) return v;                 // ya es un CSS gradient
  const cols = gradientColors(v, fallback);
  if (cols.length <= 1) return cols[0] || fallback;
  const stops = cols
    .map((col, i) => `${col} ${Math.round((i / (cols.length - 1)) * 100)}%`)
    .join(', ');
  return `linear-gradient(145deg, ${stops})`;
}

/* Primer color (para cálculo de contraste de texto) */
export function firstColor(color, fallback = '#0A0A0A') {
  const c = color || fallback;
  return (typeof c === 'string' && c.includes(',')) ? c.split(',')[0].trim() : c;
}

/* ¿El valor de color es un degradado? */
export function isGradient(color) {
  return typeof color === 'string' && color.includes(',');
}

/* Color de texto/logo adaptado al fondo de la tarjeta:
   blanco sobre fondos oscuros, negro sobre fondos claros.
   Promedia los tonos del degradado para decidir. */
function luminance(hex) {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return 0.5;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}
export function cardTextColor(color, fallback = '#1A1A1A') {
  const cols = gradientColors(color, fallback);
  const avg = cols.reduce((s, c) => s + luminance(c), 0) / cols.length;
  return avg > 0.6 ? '#0A0A0A' : '#FFFFFF';
}
